/**
 * Client configuration module for API URLs and environment settings.
 * Reads from Vite environment variables with proper fallbacks.
 *
 * Environment variables (set in .env or .env.local):
 * - VITE_APP_API_URL: Base URL for the S3 Explorer API server
 * - VITE_APP_ENVIRONMENT: Current environment (development/production)
 * - VITE_ONE_DRIVE_REDIRECT_URI: OneDrive OAuth redirect URI
 */

const isProduction = import.meta.env.MODE === 'production';

function getApiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_APP_API_URL;

  if (envUrl) {
    return envUrl;
  }

  // When running in dev mode without env variable, default to localhost
  // In production builds, this fallback should never be used
  const fallbackUrl = 'http://localhost:3000';

  if (!isProduction) {
    console.warn(
      `[S3Explorer Config] VITE_APP_API_URL not set, using fallback: ${fallbackUrl}`,
      '\nSet VITE_APP_API_URL in .env or .env.local to configure the API URL.',
    );
  }

  return fallbackUrl;
}

/**
 * API base URL for the S3 Explorer backend.
 * Reads from VITE_APP_API_URL environment variable.
 * Falls back to localhost:3000 in development with a warning.
 */
export const API_BASE_URL = getApiBaseUrl();

/**
 * Logs current configuration in non-production environments.
 * Call this during app initialization for debugging.
 */
export function logConfig(): void {
  if (!isProduction) {
    console.debug('[S3Explorer Config]', {
      API_BASE_URL,
      VITE_APP_ENVIRONMENT: import.meta.env.VITE_APP_ENVIRONMENT || 'not set',
      MODE: import.meta.env.MODE,
    });
  }
}

export default {
  API_BASE_URL,
  isProduction,
  logConfig,
};
